import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { generateQeId, Loadable } from '@ctct/ui-react';

import DiscountCode from './DiscountCode';
import InfoRow from './InfoRow';
import {
  formatDiscount,
  formatMoney,
  getEstimatedNextInvoice,
  calculateSmsLineAmount,
  calculateProratedPrepayAmount,
} from './utils';
import AddOnIds from '../../../constants/addOnIds';
import { getPlanName } from '../../../utils/orderSummaryUtils';
import { getCurrencySymbol } from '../../../utils/pricingUtils';

const OrderSummary = ({
  className,
  'data-qe-id': qeId,
  loading,
  currency,
  discountAmount,
  discountError,
  appliedDiscountCode,
  discountCode,
  setDiscountCode,
  applyDiscountCode,
  rating,
  nextBillDate,
  nextBillDateObject,
  contactTierLabel,
  smsSelectionMin,
  smsSelectionMax,
}) => {
  const { t } = useTranslation();

  /*
  from Darcy:
  The Estimated Next Invoice should be monthlyPrice + monthlyTaxPrice + (for each addOnRating) monthlyCost + monthlyTax
  Today’s sub-total should be totalPrice
  Tax should be totalTaxPrice
  Today’s total should be amountToCharge
  Note that if they have money on account (a balance) the amountToCharge will be less than the totalPrice+totalTaxPrice
  */

  const { addOnRating, amountToCharge, ratePlans, amountAppliedFromBalance, totalTaxPrice, stripeQuoteId } =
    rating || {};
  const { period, monthlyDiscount } = addOnRating?.find((addOn) => addOn.id === AddOnIds.CTCT_SMS_ADDON) ?? {};

  const integerPartPrice = (amountToCharge && Math.trunc(amountToCharge).toLocaleString()) || '0';
  const classes = classNames('utils-order-summary_V2 ctct-theme', className);
  const [ratePlan] = ratePlans || [{}];
  const defaultValue = formatMoney(0, currency);
  const currencySymbol = getCurrencySymbol(currency);

  // Detect billing system: Stripe returns stripeQuoteId, BaaS does not
  const isStripeAccount = Boolean(stripeQuoteId);

  const estimatedNextInvoice = getEstimatedNextInvoice(rating);

  // Calculate SMS line amount using shared utility (CBU-20794)
  const smsLineAmount = useMemo(() => calculateSmsLineAmount(rating, nextBillDateObject), [rating, nextBillDateObject]);

  // Calculate prorated prepay amount using shared utility
  const proratedPrepayAmount = useMemo(
    () => calculateProratedPrepayAmount(monthlyDiscount, nextBillDateObject, period),
    [monthlyDiscount, nextBillDateObject, period]
  );

  const decimalPrice = useMemo(() => {
    let retVal = '00';

    if (amountToCharge != null) {
      retVal = new Intl.NumberFormat(navigator.language, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format((amountToCharge * 100) % 100);

      if (retVal.length === 1) {
        retVal = `0${retVal}`;
      }
    }

    return retVal;
  }, [amountToCharge]);

  return (
    <div className={classes} data-qe-id={qeId} style={{ minWidth: 330 }}>
      <Loadable loading={loading} data-qe-id="paymentplan-discounts-order-summary-loading">
        <div className="order-summary-header-container" data-qe-id={generateQeId(qeId, 'header-container')}>
          <h3 className="order-summary-header" data-qe-id={generateQeId(qeId, 'header')}>
            {t('order_summary.header')}
          </h3>
        </div>

        <div className="order-summary-section-container" data-qe-id={generateQeId(qeId, 'contacts-plan')}>
          <InfoRow
            data-qe-id={generateQeId(qeId, 'sms-addon')}
            label={t('order_summary.sms-label', {
              min: smsSelectionMin.toLocaleString(),
              max: smsSelectionMax === -1 ? t('order_summary.unlimited') : smsSelectionMax.toLocaleString(),
            })}
            value={formatMoney(smsLineAmount, currency) || defaultValue}
            classBase="sms"
          />
          <div className="order-summary-next-bill-date-row">
            <InfoRow
              data-qe-id={generateQeId(qeId, 'next-bill-date')}
              label={t('order_summary.next-bill-date', { nextBillDate })}
              classBase="sms"
            />
          </div>
          {isStripeAccount && period > 1 && proratedPrepayAmount > 0 && (
            <InfoRow
              data-qe-id={generateQeId(qeId, 'sms-prepay-discount')}
              label={t('order_summary.mo-prepay-discount', { month: period })}
              value={`- ${formatMoney(proratedPrepayAmount, currency) || defaultValue}`}
              classBase="sms"
            />
          )}

          <InfoRow
            data-qe-id={generateQeId(qeId, 'taxex')}
            label={t('order_summary.estimated-taxes')}
            value={formatMoney(totalTaxPrice, currency) || defaultValue}
            classBase="tax"
          />

          {amountAppliedFromBalance > 0 && (
            <InfoRow
              data-qe-id={generateQeId(qeId, 'tax')}
              label={t('order_summary.amount-applied-from-balance')}
              value={`- ${formatMoney(amountAppliedFromBalance, currency) || defaultValue}`}
              classBase="tax"
            />
          )}
        </div>

        <div className="order-summary-section-container" data-qe-id={generateQeId(qeId, 'discount-code-container')}>
          <DiscountCode
            data-qe-id={generateQeId(qeId, 'discount-code')}
            discountCode={discountCode}
            loading={loading}
            formattedError={
              discountError ? `${discountError}. ${t('order_summary.discount-code-error-ending-info')}` : ''
            }
            discountApplied={Boolean(appliedDiscountCode && !discountError && !loading)}
            formattedDiscountCode={appliedDiscountCode}
            formattedDiscountAmount={formatDiscount(discountAmount, currency)}
            onChange={setDiscountCode}
            onApply={applyDiscountCode}
            onRemove={() => {
              applyDiscountCode(null);
              setDiscountCode('');
            }}
          />
        </div>

        <div className="order-summary-section-container" data-qe-id={generateQeId(qeId, 'section-container')}>
          <div className={classNames('order-summary-section-row', 'order-summary-total-row')}>
            <span
              className={classNames('order-summary-total-label', 'order-summary-body-left')}
              data-qe-id={generateQeId(qeId, 'total')}
            >
              {t('order_summary.todays-total')}
            </span>
            <div
              className="order-summary-body-right total-value-wrapper"
              data-qe-id={generateQeId(qeId, 'total-value')}
              style={rating ? {} : { visibility: 'hidden' }}
            >
              <span className="total-value-currency">{currencySymbol}</span>
              <span className="total-value-integer">{integerPartPrice}</span>
              <span className="total-value-decimal">{decimalPrice}</span>
            </div>
          </div>

          <InfoRow
            data-qe-id={generateQeId(qeId, 'plan-name')}
            label={t('order_summary.new-monthly-price', {
              plan: getPlanName({ planName: ratePlan?.planTypeItem?.description ?? '', header: false, onlyText: true }),
              tier: contactTierLabel,
            })}
            value={formatMoney(estimatedNextInvoice, currency) || defaultValue}
            classBase="plan"
          />
        </div>
      </Loadable>
    </div>
  );
};

OrderSummary.propTypes = {
  'data-qe-id': PropTypes.string.isRequired,
  className: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  currency: PropTypes.string,
  discountAmount: PropTypes.number,
  discountError: PropTypes.string,
  discountCode: PropTypes.string,
  appliedDiscountCode: PropTypes.string,
  setDiscountCode: PropTypes.func.isRequired,
  applyDiscountCode: PropTypes.func.isRequired,
  nextBillDate: PropTypes.string,
  contactTierLabel: PropTypes.string,
  nextBillDateObject: PropTypes.instanceOf(Date),
  smsSelectionMin: PropTypes.number.isRequired,
  smsSelectionMax: PropTypes.number.isRequired,
  rating: PropTypes.shape({
    subTotal: PropTypes.number,
    monthlyPrice: PropTypes.number,
    totalTaxPrice: PropTypes.number,
    totalPrice: PropTypes.number,
  }),
};

OrderSummary.defaultProps = {
  className: '',
  rating: null,
  currency: 'USD',
  discountAmount: 0,
  discountError: '',
  discountCode: '',
  appliedDiscountCode: '',
  nextBillDate: '',
  nextBillDateObject: null,
  contactTierLabel: '',
};

export default OrderSummary;
